class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = theme.utils.debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onInputChange.bind(this));
    facetForm.addEventListener('submit', this.onSubmitHandler.bind(this));
    facetForm.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.closest('price-range')) return;
      if (event.repeat) event.preventDefault();
      else this.onSubmitHandler(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Add event listener for price range apply button
    this.addEventListener('click', this.onPriceRangeApply.bind(this));

    // Add event listener for drawer overlay click to close drawer
    this.setupDrawerOverlayClose();
  }

  static setListeners() {
    if (FacetFiltersForm.listenersSet) return;
    FacetFiltersForm.listenersSet = true;
    const onHistoryChange = (event) => {
      FacetFiltersForm.cancelPendingSubmissions();
      FacetFiltersForm.pendingSortBy = null;
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.renderedSearchParams) {
        ++FacetFiltersForm.requestSequence;
        FacetFiltersForm.searchParamsPrev = searchParams;
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
        return;
      }
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    FacetFiltersForm.renderProductPerPage();
    FacetFiltersForm.bindDrawerControls(true);
    window.addEventListener('popstate', onHistoryChange);
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-collection-reset]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
      const form = document.querySelector('facet-filters-form');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();
      form.onActiveFilterClick(event, link);
    }, true);
  }

  static cancelPendingSubmissions() {
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      form.debouncedOnSubmit?.cancel();
    });
  }

  static bindDrawerControls(initial = false) {
    const drawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (!drawer) return;
    drawer.facetsBoundElements ||= new WeakSet();
    drawer.querySelectorAll('summary, button:not(.localization-selector):not(.selector__close-button):not(.country-filter__reset-button)').forEach((element) => {
      if (drawer.facetsBoundElements.has(element)) return;
      const handler = element.tagName === 'SUMMARY' ? drawer.onSummaryClick : drawer.onCloseButtonClick;
      if (!initial && typeof handler === 'function') element.addEventListener('click', handler.bind(drawer));
      drawer.facetsBoundElements.add(element);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true, focusResults = false) {
    FacetFiltersForm.cancelPendingSubmissions();
    const request = { id: ++FacetFiltersForm.requestSequence, searchParams, updateURLHash, focusResults };
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.setLoading(true);
    try {
      await Promise.all(FacetFiltersForm.getSections().map((section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const filterDataUrl = (element) => element.url === url;
        return FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, request)
          : FacetFiltersForm.renderSectionFromFetch(url, event, request);
      }));
      if (request.id !== FacetFiltersForm.requestSequence) return;
      if (focusResults) FacetFiltersForm.focusResults();
    } catch (error) {
      if (request.id !== FacetFiltersForm.requestSequence) return;
      FacetFiltersForm.searchParamsPrev = FacetFiltersForm.renderedSearchParams;
      if (window.location.search.slice(1) !== FacetFiltersForm.renderedSearchParams) {
        const searchParams = FacetFiltersForm.renderedSearchParams;
        history.replaceState({ searchParams }, '', `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}${window.location.hash}`);
      }
      console.error('Collection filters could not finish updating.', error);
    } finally {
      if (request.id === FacetFiltersForm.requestSequence) {
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static setLoading(loading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', String(loading));
    grid?.querySelector('.collection')?.classList.toggle('loading', loading);
    ['ProductCountResults', 'ProductCount', 'ProductCountMobile'].forEach((id) => {
      document.getElementById(id)?.classList.toggle('loading', loading);
    });
    document.querySelectorAll('.product-count .loading__spinner, .product-grid-container .loading__spinner').forEach((spinner) => {
      spinner.classList.toggle('hidden', !loading);
    });
  }

  static renderSectionFromFetch(url, event, request) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Collection section request failed (${response.status}).`);
        return response.text();
      })
      .then((responseText) => {
        if (request.id !== FacetFiltersForm.requestSequence) return;
        const html = responseText;
        FacetFiltersForm.renderSection(html, event, request);
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.filter((entry) => entry.url !== url), { html, url }];
      });
  }

  static renderSectionFromCache(filterDataUrl, event, request) {
    if (request.id !== FacetFiltersForm.requestSequence) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, request);
  }

  static renderSection(html, event, request) {
    if (request.id !== FacetFiltersForm.requestSequence) return;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    if (!parsedHTML.getElementById('ProductGridContainer') || !parsedHTML.getElementById('product-grid')) {
      throw new Error('Collection section response is missing its product grid.');
    }
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderedSearchParams = request.searchParams;
    if (request.updateURLHash) FacetFiltersForm.updateURLHash(request.searchParams);
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductCount(html);
    const selectedSort = parsedHTML.querySelector('select[name="sort_by"]')?.value || parsedHTML.querySelector('.facet-filters__sort_item.selected input[name="sort_by"]')?.value;
    if (selectedSort) {
      document.querySelectorAll('facet-filters-form [name="sort_by"]').forEach((input) => {
        if (input.tagName === 'SELECT') input.value = selectedSort;
        else input.checked = input.value === selectedSort;
      });
    }
    FacetFiltersForm.renderProductPerPage();
    FacetFiltersForm.clickGridView(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static focusResults() {
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      if (form.closest('menu-drawer')?.querySelector('details[open]')) form.closeDrawer();
    });
    document.getElementById('ProductGridContainer')?.focus({ preventScroll: true });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // Rebind 'show more swatches' buttons after grid re-render
    if (typeof initMoreSwatchButtons === 'function') {
      const container = document.getElementById('ProductGridContainer');
      initMoreSwatchButtons(container || document);
    }
  }

  static clickGridView (event) {
    document.querySelectorAll('.button--grid-view').forEach((button) => {
      if (button.facetsGridViewBound) return;
      button.facetsGridViewBound = true;
      button.addEventListener('click', this.changeLayoutGrid.bind(this));
    });
  }

  static changeLayoutGrid (event) {
    const gridView = event.currentTarget.dataset.grid;
    document.querySelectorAll('.button--grid-view').forEach((button) => {
      button.classList.add('cursor-pointer');
      button.classList.remove('active');
    });

    event.currentTarget.classList.remove('cursor-pointer');
    event.currentTarget.classList.add('active');
    document.querySelector('.product-grid-container .product-grid')?.setAttribute('data-view', gridView);
  }

  static renderProductPerPage() {
    const productGrid = document.querySelector('.product-grid-container .product-grid');

    document.querySelectorAll('input[name="per_page"]').forEach(function(input) {
      if (input.facetsPerPageBound) return;
      input.facetsPerPageBound = true;
      input.addEventListener('change', function() {
        const detailsList = input.closest('.details__list');
        const currentSelected = detailsList ? detailsList.querySelector('.selected') : null;
        if (currentSelected) currentSelected.classList.remove('selected', 'pointer-events-none');

        const currentItem = input.closest('.facet-filters__sort_item');
        if (currentItem) currentItem.classList.add('selected', 'pointer-events-none');

        const labelText = input.closest('details') ? input.closest('details').querySelector('.label-text') : null;
        if (labelText) labelText.innerHTML = this.value;

        if (detailsList) {
          detailsList.querySelectorAll('input[name="per_page"]').forEach((other) => {
            if (other !== input) other.checked = false;
          });
          detailsList.querySelectorAll('.facet-filters__sort_item').forEach((li) => {
            if (li !== currentItem) li.classList.remove('selected', 'pointer-events-none');
          });
        }

        var data = new URLSearchParams();
        data.append('attributes[pagination]', this.value);

        fetch('/cart/update.js', {
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })
        .then((response) => response.text())
        .finally(() => {
          FacetFiltersForm.renderPage(window.location.search.slice(1), null, false);
        });
      });
    });

    if (productGrid) {
      const gridViewButton = document.querySelector('.button--grid-view.active');

      if (gridViewButton) {
        productGrid.setAttribute('data-view', gridViewButton.dataset.grid);
      }
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.getElementById('ProductCountResults') || parsedHTML.getElementById('ProductCount');
    ['ProductCountResults', 'ProductCount', 'ProductCountMobile'].forEach((id) => {
      const container = document.getElementById(id);
      if (!container) return;
      if (source && container.textContent.trim() !== source.textContent.trim()) container.innerHTML = source.innerHTML;
      container.classList.remove('loading');
    });

    const loadingSpinners = document.querySelectorAll(
      '.product-count .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '#FacetsWrapperDesktop', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    FacetFiltersForm.bindDrawerControls();

    const facetFiltersForm = document.querySelector('facet-filters-form');
    if (facetFiltersForm) {
      facetFiltersForm.setupDrawerOverlayClose();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    if (new URLSearchParams(window.location.search).toString() === new URLSearchParams(searchParams).toString()) return;
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}${window.location.hash}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    searchParams.delete('per_page');
    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    return FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target.closest('form');
    if (!sourceForm?.isConnected) return;
    FacetFiltersForm.cancelPendingSubmissions();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const isMobile = sourceForm.id === 'FacetFiltersFormMobile';
    const searchParams = new URLSearchParams();
    sortFilterForms.forEach((form) => {
      const include = isMobile ? form === sourceForm : ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(form.id);
      if (!include) return;
      new URLSearchParams(this.createSearchParams(form)).forEach((value, name) => {
        if (name !== 'sort_by' && name !== 'page' && !(name.startsWith('filter.') && value === '') && !searchParams.getAll(name).includes(value)) searchParams.append(name, value);
      });
    });
    const selectedSort = FacetFiltersForm.pendingSortBy ||
      (event.target.name === 'sort_by' ? event.target.value : null) ||
      new URLSearchParams(window.location.search).get('sort_by') ||
      sourceForm.querySelector('select[name="sort_by"]')?.value ||
      document.querySelector('.facet-filters__sort_item.selected input[name="sort_by"]')?.value;
    if (selectedSort) searchParams.set('sort_by', selectedSort);
    FacetFiltersForm.pendingSortBy = null;
    return this.onSubmitForm(searchParams.toString(), event);
  }

  onInputChange(event) {
    if (event.target.name === 'per_page') return;
    if (event.target.name === 'sort_by') FacetFiltersForm.pendingSortBy = event.target.value;
    // Check if the input is a price range input
    const isPriceRangeInput = event.target.classList.contains('filter__price_change') ||
                             event.target.classList.contains('filter__price_number');

    // Only auto-submit if it's not a price range input
    if (!isPriceRangeInput) {
      ++FacetFiltersForm.requestSequence;
      this.debouncedOnSubmit(event);
    }
  }

  onPriceRangeApply(event) {
    // Check if the clicked element is the price range apply button
    if (event.target.closest('.facets-price__apply button')) {
      return this.onSubmitHandler(event);
    }
  }

  onActiveFilterClick(event, link = event.currentTarget) {
    event.preventDefault();
    FacetFiltersForm.cancelPendingSubmissions();
    FacetFiltersForm.pendingSortBy = null;
    FacetFiltersForm.toggleActiveFacets();
    const searchParams = new URL(link.href, window.location.href).searchParams;
    const currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) searchParams.set('sort_by', currentSort);
    searchParams.delete('page');
    searchParams.delete('per_page');
    const focusResults = link.hasAttribute('data-collection-reset') || link.classList.contains('active-facets__button-remove') || link.classList.contains('mobile-facets__clear');
    const result = FacetFiltersForm.renderPage(searchParams.toString(), null, true, focusResults);

    if(window.innerWidth <= 1024) {
      document.querySelectorAll('.mobile-facets__details').forEach(item => {
        item.removeAttribute('style');
        item.removeAttribute('open')
      })
    }
    return result;
  }

  setupDrawerOverlayClose() {
    const drawerOverlay = this.querySelector('[data-drawer-overlay]');
    if (drawerOverlay && !drawerOverlay.facetOverlayBound) {
      drawerOverlay.facetOverlayBound = true;
      drawerOverlay.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeDrawer();
      });
    }
  }

  closeDrawer() {
    const menuDrawer = this.closest('menu-drawer');
    const details = menuDrawer?.querySelector('details');
    if (!details?.hasAttribute('open')) return;
    if (typeof menuDrawer.closeMenuDrawer === 'function') {
      menuDrawer.closeMenuDrawer(new Event('click'));
    } else {
      details.removeAttribute('open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
    }
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile');
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestSequence = 0;
FacetFiltersForm.renderedSearchParams = window.location.search.slice(1);
FacetFiltersForm.pendingSortBy = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeSliderPrice();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();

    const numberS = this.querySelectorAll('.filter__price_change');
    let value1 = numberS[0].value;
    let value2 = numberS[1].value;
    this.updateDisplay(value1, value2);
  }

  onRangeChange(event) {
    // this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    // Note: Form submission is now handled by the apply button click
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    // if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    // if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  // adjustToValidValues(input) {
  //   const value = Number(input.value);
  //   const min = Number(input.getAttribute('data-min'));
  //   const max = Number(input.getAttribute('data-max'));

  //   if (value < min) input.value = min;
  //   if (value > max) input.value = max;
  // }

  rangeSliderPrice() {
    let rangeS = this.querySelectorAll('.filter__price_change'),
        numberS = this.querySelectorAll('.filter__price_number'),
        isFireFox = typeof InstallTrigger !== 'undefined',
        lowerSlider = rangeS[0],
        upperSlider = rangeS[1];

    rangeS.forEach(element => {
      element.oninput = () => {
        let slide1 = Math.floor(rangeS[0].value),
          slide2 = Math.ceil(rangeS[1].value),
          slide = Math.round(Number(this.querySelector('.filter__price_change').max) / 100 * (72 / (this.offsetWidth / 100)));

        if (slide1 > slide2 - slide) {
          if (slide2 != upperSlider.max) {
            upperSlider.value = slide1 + slide;
            numberS[0].value = lowerSlider.value;
            numberS[1].value = slide1 + slide;

            if (slide2 == upperSlider.max) {
              lowerSlider.value = parseInt(upperSlider.max) - slide;
              numberS[0].value = parseInt(upperSlider.max) - slide;
            }
          }
        }

        if (slide2 < slide1 + slide) {
          if (slide1 == lowerSlider.min) return;
          lowerSlider.value = slide2 - slide;
          numberS[0].value = slide2 - slide;
          numberS[1].value = upperSlider.value;

          if (slide1 == lowerSlider.min) {
            upperSlider.value = slide;
            numberS[1].value = slide;
          }
        }

        if (slide2 < slide1 + slide && slide2 == upperSlider.max) return;

        numberS[0].value = slide1;
        numberS[1].value = slide2;
        if (!isFireFox) this.updateDisplay(numberS[0].value, numberS[1].value);
      }
    });

    numberS.forEach((element) => {
      element.oninput = () => {
        var number1 = parseFloat(numberS[0].placeholder),
            checkValue1 = number1 != number1,
            number2 = parseFloat(numberS[1].placeholder),
            checkValue2 = number2 != number2;

        if (!checkValue1) {
          rangeS[0].placeholder = number1;
        }

        if (!checkValue2) {
          rangeS[1].placeholder = number2;
        }

        if (number1 > number2) {
          if (!isFireFox) this.updateDisplay(number2, number1);
        } else {
          if (!isFireFox) this.updateDisplay(number1, number2);
        }
      }
    });
  }

  updateDisplay(value1, value2) {
    const priceRange = this.querySelector('.facets-price__range');
    const max = this.querySelector('.filter__price_change').max;
    const width = priceRange.clientWidth;

    priceRange.style.setProperty('--left-space', (parseInt(value1) / parseInt(max)) * width + 'px');
    priceRange.style.setProperty('--right-space', (width - (parseInt(value2) / parseInt(max)) * width) + 'px');
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);
